import { useEffect, useRef, useState, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useReducedMotion } from './useReducedMotion';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

/**
 * Production-ready hook for scroll-triggered animations
 * Consolidates fade, stagger, parallax, and zoom animations into a single API
 */
export const useScrollAnimation = ({
  type = 'fade',
  scope = null,
  duration = 0.8,
  delay = 0,
  stagger = 0.2,
  ease = 'power3.out',
  trigger = 'top 75%',
  from = { y: 40, x: 0, opacity: 0, scale: 1 },
  to = { y: 0, x: 0, opacity: 1, scale: 1 },
  markers = false,
  onComplete = null,
  onStart = null
} = {}) => {
  const sectionRef = useRef(null);
  const contextRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  // Cleanup function
  const cleanup = useCallback(() => {
    if (contextRef.current) {
      contextRef.current.revert();
      contextRef.current = null;
    }
    setIsAnimating(false);
  }, []);

  // Setup animation based on type
  const setupAnimation = useCallback(() => {
    if (!sectionRef.current || prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    let timeline;
    let scrollTrigger;
    
    const ctx = gsap.context(() => {
      switch (type) {
        case 'fade':
          // Simple fade-in animation
          gsap.set(sectionRef.current, from);
          
          timeline = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              start: trigger,
              toggleActions: 'play none none reverse',
              markers,
              onEnter: () => {
                setIsVisible(true);
                setIsAnimating(true);
                onStart?.();
              },
              onLeave: () => setIsAnimating(false),
              onEnterBack: () => setIsAnimating(true),
              onLeaveBack: () => setIsAnimating(false)
            }
          });

          timeline.to(sectionRef.current, {
            ...to,
            duration,
            delay,
            ease,
            onComplete: () => {
              setIsAnimating(false);
              onComplete?.();
            }
          });
          break;

        case 'stagger':
          // Stagger animation for child elements
          if (!scope) {
            console.warn('useScrollAnimation: scope is required for stagger animations');
            return;
          }

          const children = sectionRef.current.querySelectorAll(scope);
          if (children.length === 0) return;

          gsap.set(children, from);

          timeline = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              start: trigger,
              toggleActions: 'play none none reverse',
              markers,
              onEnter: () => {
                setIsVisible(true);
                setIsAnimating(true);
                onStart?.();
              },
              onLeave: () => setIsAnimating(false),
              onEnterBack: () => setIsAnimating(true),
              onLeaveBack: () => setIsAnimating(false)
            }
          });

          timeline.to(children, {
            ...to,
            duration,
            stagger,
            ease,
            onComplete: () => {
              setIsAnimating(false);
              onComplete?.();
            }
          });
          break;

        case 'parallax':
          // Parallax effect
          gsap.set(sectionRef.current, from);

          timeline = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              start: trigger,
              end: 'bottom top',
              scrub: true,
              markers,
              onEnter: () => {
                setIsVisible(true);
                setIsAnimating(true);
                onStart?.();
              },
              onLeave: () => setIsAnimating(false),
              onEnterBack: () => setIsAnimating(true),
              onLeaveBack: () => setIsAnimating(false)
            }
          });

          timeline.to(sectionRef.current, {
            ...to,
            duration: 1,
            ease: 'none'
          });
          break;

        case 'zoom':
          // Zoom in/out effect
          gsap.set(sectionRef.current, from);

          timeline = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              start: trigger,
              toggleActions: 'play none none reverse',
              markers,
              onEnter: () => {
                setIsVisible(true);
                setIsAnimating(true);
                onStart?.();
              },
              onLeave: () => setIsAnimating(false),
              onEnterBack: () => setIsAnimating(true),
              onLeaveBack: () => setIsAnimating(false)
            }
          });

          timeline.to(sectionRef.current, {
            ...to,
            duration,
            delay,
            ease,
            onComplete: () => {
              setIsAnimating(false);
              onComplete?.();
            }
          });
          break;

        default:
          console.warn(`useScrollAnimation: Unknown animation type "${type}"`);
          return;
      }

    }, sectionRef);
    
    // Store context for cleanup
    contextRef.current = ctx;
    
    return ctx;
  }, [type, scope, duration, delay, stagger, ease, trigger, from, to, markers, onComplete, onStart, prefersReducedMotion]);

  // Setup animations when component mounts or dependencies change
  useEffect(() => {
    if (!sectionRef.current) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      setupAnimation();
    }, 100);

    return () => {
      clearTimeout(timer);
      cleanup();
    };
  }, [setupAnimation, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Animation control functions
  const play = useCallback(() => {
    if (contextRef.current && !prefersReducedMotion) {
      gsap.to(sectionRef.current, { ...to, duration, ease });
    }
  }, [to, duration, ease, prefersReducedMotion]);

  const pause = useCallback(() => {
    if (contextRef.current) {
      gsap.getTweensOf(sectionRef.current).forEach(tween => tween.pause());
    }
  }, []);

  const restart = useCallback(() => {
    if (contextRef.current && !prefersReducedMotion) {
      gsap.set(sectionRef.current, from);
      gsap.to(sectionRef.current, { ...to, duration, ease });
    }
  }, [from, to, duration, ease, prefersReducedMotion]);

  return {
    ref: sectionRef,
    isAnimating,
    isVisible,
    prefersReducedMotion,
    play,
    pause,
    restart
  };
};

export default useScrollAnimation;
